import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ListMusic, ListOrdered, X } from 'lucide-react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from '@/lib/utils'
import { useSidebar } from '@/lib/SidebarContext'
import { AppLink } from '@/components/ui/app-link'
import { useBottomSheet } from '@/components/ui/bottom-sheet'
import { useOptionalPlayer, type PlayerControls, type PlayerState } from './PlayerProvider'
import { ProgressBar, chapterAt, formatClock } from './ProgressBar'
import { NextButton, PlayPauseButton, PlayerIconButton, PreviousButton, QueueButton, QueueList, SkipButton, SleepMenu, SpeedMenu, VolumeControl } from './PlayerControls'
import { ChapterList, EpisodeCover } from './PodcastPlayer'
import { paletteStyle, useCoverPalette } from './coverColor'
import { PlayerToneContext } from './tone'

const DRAG_CLOSE_PX = 90

/** One line that scrolls back and forth when it does not fit (still under reduced motion: it ellipses). */
function MarqueeText({ children, className }: { children: React.ReactNode; className?: string }) {
  const boxRef = React.useRef<HTMLSpanElement | null>(null)
  const textRef = React.useRef<HTMLSpanElement | null>(null)
  const [shift, setShift] = React.useState(0)
  React.useEffect(() => {
    const box = boxRef.current
    const text = textRef.current
    if (!box || !text) return
    const measure = () => setShift(Math.max(0, text.scrollWidth - box.clientWidth))
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(box)
    observer.observe(text)
    return () => observer.disconnect()
  }, [children])
  return (
    <span ref={boxRef} className={cn('block overflow-hidden whitespace-nowrap', shift > 0 && 'motion-reduce:text-ellipsis', className)}>
      <span
        ref={textRef}
        className={cn('inline-block', shift > 0 && 'player-marquee motion-reduce:block motion-reduce:truncate')}
        style={shift > 0 ? { ['--marquee-shift' as string]: `-${shift}px` } : undefined}
      >
        {children}
      </span>
    </span>
  )
}

type Player = PlayerState & PlayerControls

/**
 * The phone's now-playing sheet: full screen in the cover's color, the cover
 * large, the controls big, and the chapters and the queue below. It closes
 * by swiping down, by the chevron, with Esc and with the system "back".
 */
function NowPlayingSheet({ player, sheet, palette, href }: {
  player: Player
  sheet: ReturnType<typeof useBottomSheet>
  palette: ReturnType<typeof useCoverPalette>
  href: string | null
}) {
  const { t } = useTranslation('player')
  const [drag, setDrag] = React.useState(0)
  const [panel, setPanel] = React.useState<'chapters' | 'queue' | null>(null)
  const start = React.useRef<number | null>(null)
  const contentRef = React.useRef<HTMLDivElement | null>(null)
  const { current, position, duration, coverUrl, seek, status, stop } = player
  if (!current) return null
  const total = duration || current.durationSeconds || 0
  const chapters = current.chapters || []
  const chapter = chapterAt(chapters, position)

  const onTouchStart = (e: React.TouchEvent) => { start.current = (contentRef.current?.scrollTop ?? 0) <= 0 ? e.touches[0]?.clientY ?? null : null }
  const onTouchMove = (e: React.TouchEvent) => {
    if (start.current === null) return
    setDrag(Math.max(0, (e.touches[0]?.clientY ?? start.current) - start.current))
  }
  const onTouchEnd = () => {
    if (start.current === null) return
    start.current = null
    if (drag > DRAG_CLOSE_PX) sheet.hide()
    setDrag(0)
  }
  const toggle = (next: 'chapters' | 'queue') => setPanel((open) => (open === next ? null : next))

  return (
    <DialogPrimitive.Root open={sheet.open} onOpenChange={(open) => { if (!open) sheet.hide() }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          ref={contentRef}
          aria-describedby={undefined}
          data-now-playing
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          className={cn(
            'fixed inset-0 z-[70] overflow-y-auto text-white outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom data-[state=open]:duration-300 motion-reduce:animate-none'
          )}
          style={{
            ...paletteStyle(palette),
            background: 'linear-gradient(180deg, rgb(var(--pl-hero)) 0%, rgb(var(--pl-hero-deep)) 70%)',
            ...(drag ? { transform: `translateY(${drag}px)`, transition: 'none' } : null)
          }}
        >
          <DialogPrimitive.Title className="sr-only">{t('miniPlayer')}</DialogPrimitive.Title>
          <PlayerToneContext.Provider value="onColor">
            <div className="mx-auto flex min-h-full max-w-md flex-col px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[max(0.5rem,env(safe-area-inset-top))]">
              <div className="flex items-center justify-between">
                <PlayerIconButton label={t('collapse')} onClick={() => sheet.hide()}>
                  <ChevronDown className="h-6 w-6" />
                </PlayerIconButton>
                <p className="min-w-0 truncate text-xs font-semibold uppercase tracking-wider text-white/90">{current.show?.title || t('miniPlayer')}</p>
                <PlayerIconButton label={t('close')} onClick={() => sheet.hide(stop)}>
                  <X className="h-5 w-5" />
                </PlayerIconButton>
              </div>

              <EpisodeCover url={coverUrl} title={current.title} className="mx-auto mt-6 w-full max-w-[22rem] rounded-lg bg-[rgb(var(--pl-on-hero)/0.15)] text-white shadow-[0_12px_48px_rgba(0,0,0,0.5)]" />

              <div className="mt-8 min-w-0">
                {href ? (
                  <AppLink to={href} onClick={() => sheet.hide()} className="block rounded-sm text-2xl font-bold leading-tight text-white line-clamp-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--pl-on-hero))]">{current.title}</AppLink>
                ) : (
                  <p className="text-2xl font-bold leading-tight line-clamp-2">{current.title}</p>
                )}
                <p className="mt-1 truncate text-sm text-white/90">{chapter ? chapter.title : current.show?.title}</p>
              </div>

              <div className="mt-5">
                <ProgressBar position={position} duration={total} chapters={chapters} onSeek={seek} />
                <div className="flex justify-between text-xs tabular-nums text-white/90">
                  <span>{formatClock(position)}</span>
                  <span>{total ? `−${formatClock(Math.max(0, total - position))}` : '--:--'}</span>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <PreviousButton />
                <SkipButton direction="back" />
                <PlayPauseButton size="xl" />
                <SkipButton direction="forward" />
                <NextButton />
              </div>

              <div className="mt-4 flex items-center justify-between">
                <SpeedMenu />
                <SleepMenu />
                {chapters.length > 0 && (
                  <PlayerIconButton label={t('chapters.title', { count: chapters.length })} active={panel === 'chapters'} aria-expanded={panel === 'chapters'} onClick={() => toggle('chapters')}>
                    <ListOrdered className="h-5 w-5" />
                  </PlayerIconButton>
                )}
                {player.canSync && (
                  <PlayerIconButton label={t('queue.open', { count: player.queue.length })} active={panel === 'queue'} aria-expanded={panel === 'queue'} onClick={() => toggle('queue')}>
                    <ListMusic className="h-5 w-5" />
                  </PlayerIconButton>
                )}
              </div>

              {panel && (
                <PlayerToneContext.Provider value="surface">
                  <div className="mt-5 rounded-xl bg-surface p-2 text-foreground shadow-lg">
                    {panel === 'chapters'
                      ? <ChapterList chapters={chapters} current={chapter} playing={status === 'playing'} onSeek={seek} />
                      : <QueueList />}
                  </div>
                </PlayerToneContext.Provider>
              )}
            </div>
          </PlayerToneContext.Provider>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

/**
 * F-178 criterion 7: the mini-player, fixed at the bottom of the shell while
 * an episode is loaded and its full player is not on screen. It keeps the
 * sound going across pages and spaces. Desktop: Spotify's bottom bar (the
 * episode on the left, the controls and the bar in the middle, speed, sleep,
 * queue and volume on the right). Phones: a compact bar in the cover's color
 * that opens the full-screen now-playing sheet.
 *
 * It publishes its height as `--player-h` and marks `<html data-player-open>`,
 * so `<main>` (`.dock-clear`), the pages' own bottom bars and the chat dock
 * move up by that much (index.css): nothing it covers is out of reach, the
 * comment field included.
 */
export function MiniPlayer() {
  const player = useOptionalPlayer()
  const { t } = useTranslation('player')
  const { isCollapsed } = useSidebar()
  const rootRef = React.useRef<HTMLDivElement | null>(null)
  const visible = Boolean(player?.current) && !player?.fullPlayerVisible
  const palette = useCoverPalette(player?.coverUrl)
  const sheet = useBottomSheet()

  React.useEffect(() => {
    const root = document.documentElement
    const el = rootRef.current
    if (!visible || !el) {
      root.style.setProperty('--player-h', '0px')
      root.removeAttribute('data-player-open')
      return
    }
    const publish = () => root.style.setProperty('--player-h', `${Math.ceil(el.getBoundingClientRect().height)}px`)
    publish()
    root.setAttribute('data-player-open', '')
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(publish)
    observer.observe(el)
    return () => observer.disconnect()
  }, [visible])

  React.useEffect(() => () => {
    document.documentElement.style.removeProperty('--player-h')
    document.documentElement.removeAttribute('data-player-open')
  }, [])

  /* The episode went away (closed, or its full player came on screen): the sheet goes with it. */
  const { open: sheetOpen, hide: hideSheet } = sheet
  React.useEffect(() => { if (!visible && sheetOpen) hideSheet() }, [visible, sheetOpen, hideSheet])

  if (!player || !player.current || !visible) return null
  const { current, position, duration, coverUrl, seek, stop, episodeHref } = player
  const total = duration || current.durationSeconds || 0
  const href = episodeHref(current)
  const chapter = chapterAt(current.chapters, position)
  const subtitle = chapter ? chapter.title : current.show?.title || ''
  const percent = total ? Math.min(100, (position / total) * 100) : 0

  const title = href ? (
    <AppLink to={href} className="block truncate rounded-sm text-sm font-semibold text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--pl-accent))]">{current.title}</AppLink>
  ) : (
    <p className="truncate text-sm font-semibold text-foreground">{current.title}</p>
  )

  return (
    <div
      ref={rootRef}
      data-mini-player
      role="region"
      aria-label={t('miniPlayer')}
      style={paletteStyle(palette)}
      className={cn(
        'pointer-events-none fixed inset-x-0 bottom-0 z-30 pb-[env(safe-area-inset-bottom)] animate-in fade-in-0 slide-in-from-bottom-4 duration-300 motion-reduce:animate-none',
        'lg:pointer-events-auto lg:border-t lg:border-border lg:bg-surface/95 lg:shadow-[0_-4px_16px_rgba(15,23,42,0.08)] lg:backdrop-blur',
        !isCollapsed && 'lg:left-[--sidebar-width]'
      )}
    >
      {/* Phones: the compact bar in the cover's color; tapping it opens the now-playing sheet. */}
      <PlayerToneContext.Provider value="onColor">
        <div className="pointer-events-auto px-2 pb-2 lg:hidden">
          <div
            className="relative overflow-hidden rounded-lg text-white shadow-lg"
            style={{ background: 'linear-gradient(90deg, rgb(var(--pl-hero)) 0%, rgb(var(--pl-hero-deep)) 100%)' }}
          >
            <div className="absolute inset-x-0 top-0 h-0.5 bg-[rgb(var(--pl-on-hero)/0.25)]" aria-hidden>
              <div className="h-full bg-[rgb(var(--pl-on-hero))]" style={{ width: `${percent}%` }} />
            </div>
            <div className="flex items-center gap-1 py-1.5 pl-1.5 pr-1">
              <button
                type="button"
                data-mini-expand
                onClick={sheet.show}
                aria-label={t('expand')}
                aria-haspopup="dialog"
                className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--pl-on-hero))]"
              >
                <EpisodeCover url={coverUrl} title={current.title} className="w-10 rounded bg-[rgb(var(--pl-on-hero)/0.15)] text-white" />
                <span className="min-w-0 flex-1">
                  <MarqueeText className="text-sm font-semibold">{current.title}</MarqueeText>
                  {subtitle && <span className="block truncate text-xs text-white/90">{subtitle}</span>}
                </span>
              </button>
              <PlayPauseButton size="sm" className="mx-1 shadow-none" />
              <PlayerIconButton label={t('close')} onClick={stop}>
                <X className="h-5 w-5" />
              </PlayerIconButton>
            </div>
          </div>
        </div>
      </PlayerToneContext.Provider>
      <NowPlayingSheet player={player} sheet={sheet} palette={palette} href={href} />

      {/* Desktop: Spotify's bottom bar. */}
      <div
        className="hidden items-center gap-4 px-4 py-2 lg:flex"
        style={{ backgroundImage: 'linear-gradient(90deg, rgb(var(--pl-accent) / 0.1) 0%, transparent 35%)' }}
      >
        <div className="flex min-w-0 flex-[3_1_0%] items-center gap-3">
          <EpisodeCover url={coverUrl} title={current.title} className="w-14 rounded-md shadow-md" />
          <div className="min-w-0">
            {title}
            {subtitle && <p className="truncate text-xs text-muted-foreground">{subtitle}</p>}
          </div>
        </div>

        <div className="flex min-w-0 flex-[4_1_0%] flex-col items-center">
          <div className="flex items-center gap-1">
            <PreviousButton className="h-10 w-10" />
            <SkipButton direction="back" className="h-10 w-10" />
            <PlayPauseButton size="md" className="mx-1" />
            <SkipButton direction="forward" className="h-10 w-10" />
            <NextButton className="h-10 w-10" />
          </div>
          <div className="flex w-full max-w-xl items-center gap-2 text-[11px] tabular-nums text-muted-foreground">
            <span className="w-12 text-right">{formatClock(position)}</span>
            <ProgressBar position={position} duration={total} chapters={current.chapters} onSeek={seek} variant="thin" className="flex-1" />
            <span className="w-12">{total ? formatClock(total) : '--:--'}</span>
          </div>
        </div>

        <div className="flex min-w-0 flex-[3_1_0%] items-center justify-end gap-0.5">
          <SpeedMenu />
          <SleepMenu />
          <QueueButton />
          <VolumeControl className="hidden xl:flex" />
          <PlayerIconButton label={t('close')} onClick={stop}>
            <X className="h-5 w-5" />
          </PlayerIconButton>
        </div>
      </div>
    </div>
  )
}
